import { Formik } from 'formik';
import React, { useRef } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Field, Form, FormMessage, ModalCard } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm } from '~/hooks';
import { ErrorPage } from '~/routes/public/pages';

const P = styled.p`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

export default function ProjectTaskChangeTypeConfirmation({ task, onClose, onChanged }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ status, message, isSubmitting }, form] = useForm();
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  if (!task) return <ErrorPage.NotFound publicSite={false} />;

  const targetMode = task.mode === 'group' ? 'standard' : 'group';
  const title = targetMode === 'group' ? 'Change to Group Task' : 'Change to Standard Task';

  async function handleSubmit(values) {
    try {
      form.submit();
      await api.www
        .workspaces(workspace.id)
        .projects(task.projectId)
        .tasks(task.id)
        .setTaskType({ projectTaskTypeId: values.taskType?.id ?? null });
      await onChanged();
      form.done();
    } catch ({ message }) {
      form.error({ message });
    }
  }

  const initialValues = { taskType: null };
  const handleClose = () => dirtyCheck(onClose);

  return (
    <ModalCard title={title} onClose={onClose}>
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          taskType: Yup.object().label('Task Type').nullable().required(),
        })}>
        {() => (
          <Form>
            <ModalCard.Body>
              <P>
                This will change the <strong>{task.name}</strong> task from a {task.mode} task to a {targetMode} task.
                Select the new task type below.
              </P>
              <Form.Control>
                <Field.ProjectTaskTypeSelect
                  autoFocus
                  name="taskType"
                  placeholder={targetMode === 'group' ? 'Group Task Type' : 'Standard Task Type'}
                  mode={targetMode}
                  clearable={false}
                />
              </Form.Control>
              {status && <FormMessage.Error>{message || 'An error has occurred.'}</FormMessage.Error>}
            </ModalCard.Body>
            <ModalCard.Footer>
              <Buttons align="right">
                <CancelButton onClick={handleClose}>Close</CancelButton>
                <Button type="submit" isLoading={isSubmitting}>
                  Change
                </Button>
              </Buttons>
            </ModalCard.Footer>
          </Form>
        )}
      </Formik>
    </ModalCard>
  );
}
